/**
 * Model_Router executor — the impure ring around the pure
 * `decide` / `reduce` core.
 *
 * The pure state machine answers "which model do I try next?" and
 * "what does my health look like after this event?". The executor is
 * what actually drives a sequence of HTTPS calls through the
 * Fallback_Chain, applies the wall-clock waits between attempts,
 * threads streaming events out to the UI, and converts every error
 * surface into an event so the chat layer never sees a thrown
 * exception (Requirement 5.6).
 *
 * Per-event behaviour against the Gemini client (Requirements 5.3,
 * 5.4, 5.5, 5.8, 5.9, 12.3, 12.4):
 *
 *   chunk             → forwarded to the UI as `{ kind: 'chunk' }`
 *                       and accumulated locally so a later
 *                       streamInterrupted retry can replace it.
 *   complete          → `reduce({ kind: 'success' })`, then yield
 *                       `final` (or `streamReplaced` if a previous
 *                       attempt had streamed partial text that the UI
 *                       must swap for the complete reply).
 *   rateLimit         → `reduce({ kind: 'rateLimit' })`. The pure
 *                       reducer applies the Retry-After cooldown (or
 *                       the 60-second default). The executor
 *                       immediately re-decides; no extra delay
 *                       (Requirement 5.3).
 *   transient         → `reduce({ kind: 'transient' })` and a fixed
 *                       250 ms backoff before the next attempt
 *                       (Requirement 5.4).
 *   safetyBlock       → `reduce({ kind: 'safetyBlock' })`. At most ONE
 *                       retry on the next eligible model — on the
 *                       second consecutive safety block within the
 *                       same execute() call we surface softFallback
 *                       (Requirement 12.3 / Property 17).
 *   streamInterrupted → discard the partial, switch the next attempt
 *                       to non-streaming, and on its successful
 *                       complete emit `streamReplaced` so the UI swaps
 *                       the partial bubble for the full reply
 *                       (Requirements 5.9 / Property 15).
 *
 * If `decide` returns `giveUp` (no model is currently eligible and no
 * forcedModel is set), the executor sleeps until the soonest cooldown
 * (capped at 30 seconds) and retries once. A second giveUp surfaces a
 * single `softFallback` event (Requirement 5.5 / Property 14).
 *
 * Sliding-window counter expiry is a wall-clock concern, not a state-
 * machine transition, so the executor maintains a side map of recent
 * success timestamps per model and overwrites the counters on
 * `state.health[m].requestsLastMinute / requestsLastDay` directly each
 * loop iteration. The `decide` / `reduce` pure core remains untouched.
 *
 * Validates: Requirements 5.3, 5.4, 5.5, 5.6, 5.8, 5.9, 12.3, 12.4.
 */

import type {
  ModelHealth,
  ModelId,
  RouterEvent,
  RouterResult,
  RouterState,
} from './types';
import type {
  GeminiContent,
  GeminiRequest,
  GeminiResponseEvent,
} from '../../infra/geminiClient';
import { getApiKey, sendGeminiRequest, advanceApiKey, getApiKeyCount } from '../../infra/geminiClient';
import { decide } from './decide';
import { reduce } from './reduce';
import { FALLBACK_CHAIN, MODEL_LIMITS } from './fallbackChain';
import {
  loadPersistedHealth,
  savePersistedHealth,
  type PersistedModelEntry,
} from './persistence';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * One event yielded by `execute()`.
 *
 * The Chat_Client renders `chunk` and `streamReplaced` directly into
 * the assistant bubble, finalizes the bubble on `final`, and shows the
 * inline soft-fallback notice on `softFallback`.
 *
 * `streamReplaced` differs from `final` only in semantics: it tells the
 * UI to throw away whatever partial text the previous streaming attempt
 * had pushed and replace it with `fullText` from the non-streaming
 * retry (Requirement 5.9 / Property 15).
 */
export interface ExecutorEvent {
  kind: 'chunk' | 'streamReplaced' | 'final' | 'softFallback';
  /** For 'chunk': the new token text. */
  text?: string;
  /** For 'streamReplaced': the full assembled text replacing prior partial. */
  fullText?: string;
  /** For 'final': the complete final text and the model that produced it. */
  finalText?: string;
  modelUsed?: ModelId;
}

/**
 * Input to one `execute()` call.
 *
 * `stream === true` opts into streaming for the FIRST attempt; if that
 * stream is interrupted the executor automatically switches to
 * non-streaming for subsequent retries on later models.
 *
 * `signal` is the caller's cancellation surface; it is forwarded into
 * every underlying `sendGeminiRequest` so an external abort cancels
 * the in-flight HTTP request without the executor having to throw.
 */
export interface ExecuteRequestArgs {
  systemPrompt: string;
  contents: GeminiContent[];
  /** Whether to attempt streaming. Falls back to non-streaming on stream interruption. */
  stream: boolean;
  /** Caller's signal for cancellation. */
  signal?: AbortSignal;
  /**
   * When true, attach Gemini's Google Search grounding tool so the
   * model can ground its answer in fresh web results. Free-tier
   * support is model-dependent; the executor still falls back to the
   * next chain entry if the active model rejects the tool.
   */
  useWebSearch?: boolean;
}

/**
 * Public surface of an executor instance.
 *
 * `getState` is read-only by convention — callers MUST NOT mutate the
 * returned object; the executor returns its own internal state by
 * reference for cheap diagnostics-view rendering, and the diagnostics
 * view is read-only.
 */
export interface ModelRouterExecutor {
  /** Get a snapshot of router state. */
  getState(): RouterState;
  /** Set the dev forced-model override. */
  setForcedModel(modelId: ModelId | null): void;
  /** Run a request through the fallback chain. Yields events. */
  execute(args: ExecuteRequestArgs): AsyncGenerator<ExecutorEvent>;
  /** One-shot convenience for the summarizer (Task 4.3). Returns final RouterResult only. */
  oneShot(args: {
    systemPrompt: string;
    userText: string;
    preferredModel?: ModelId;
  }): Promise<RouterResult>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Sliding window for `requestsLastMinute` (60 seconds). */
const ONE_MINUTE_MS = 60_000;

/** Sliding window for `requestsLastDay` (24 hours). */
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** Fixed backoff between transient failures (Requirement 5.4). */
const TRANSIENT_BACKOFF_MS = 250;

/**
 * Per-model skip window applied (in-memory only) after a transient
 * failure (HTTP 5xx, network error, malformed body, stream interruption)
 * during a single `execute()` run.
 *
 * The pure reducer deliberately sets no cooldown on `transient`
 * (Requirement 5.4) because the failure may be a local network blip
 * rather than a model-specific outage. Without *some* short skip,
 * though, `decide` keeps re-selecting the very same first model every
 * loop iteration — so a model that reliably 503s (e.g. gemini-3.5-flash
 * has been observed doing this) would trap the executor and the chain
 * would never advance to a healthy model.
 *
 * 20 s is long enough to step past a flaky model for the current turn
 * yet short enough that a transient blip doesn't bench the model for
 * the rest of the session. This cooldown is written straight onto the
 * in-memory state and is NOT persisted, so a page reload clears it.
 */
const TRANSIENT_SKIP_MS = 20_000;

/**
 * Hard upper bound on attempt iterations within one `execute()` run.
 *
 * Defense-in-depth: even with transient skips and rate-limit cooldowns
 * advancing the chain, this guarantees the generator always terminates
 * (with a soft-fallback) instead of spinning forever under a pathological
 * mix of failures. Sized generously at 4× the chain length so a healthy
 * model is always reachable in normal operation.
 */
const MAX_TOTAL_ATTEMPTS = FALLBACK_CHAIN.length * 4;

/**
 * Upper bound on the giveUp-then-retry sleep (Requirement 5.5).
 *
 * Reduced from 30s to 5s: a model whose Retry-After header pushed
 * cooldown out by minutes would block the UI for a long time;
 * 5s keeps the soft-fallback latency snappy.
 */
const MAX_GIVEUP_WAIT_MS = 5_000;

/**
 * Maximum number of safety blocks tolerated within a single
 * `execute()` invocation (Requirement 12.3 / Property 17).
 *
 * Increased from 2 to 5: every model in the chain gets a chance
 * before we surface softFallback. A safety block on one model often
 * succeeds on the next (different model versions calibrate safety
 * differently), so giving up after 2 blocks was too aggressive and
 * caused spurious "Couldn't deliver" bubbles for harmless prompts.
 */
const MAX_SAFETY_BLOCKS = 5;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Default sleep used when the caller does not inject a fake. Resolves
 * synchronously when `ms <= 0` so a `decide` returning `wait { waitMs:
 * 0 }` does not yield to the macrotask queue unnecessarily.
 */
function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    if (ms <= 0) {
      resolve();
      return;
    }
    setTimeout(resolve, ms);
  });
}

/**
 * Build a fully populated `Record<ModelId, ModelHealth>` from the
 * declared `FALLBACK_CHAIN` and `MODEL_LIMITS` constants.
 *
 * Every ModelId is a member of the chain by design, so this seeds an
 * entry for each. `cooldownUntilMs`, `requestsLastMinute`, and
 * `requestsLastDay` start at `0` per the design spec.
 */
function buildInitialHealth(): Record<ModelId, ModelHealth> {
  const health = {} as Record<ModelId, ModelHealth>;
  for (const modelId of FALLBACK_CHAIN) {
    const limits = MODEL_LIMITS[modelId];
    if (limits === undefined) {
      continue;
    }
    health[modelId] = {
      modelId,
      cooldownUntilMs: 0,
      requestsLastMinute: 0,
      requestsLastDay: 0,
      rpmLimit: limits.rpm,
      rpdLimit: limits.rpd,
    };
  }
  return health;
}

/**
 * Build the side-map of recent-success timestamps used for sliding-
 * window counter refresh. Keys are pre-populated for every model in
 * the chain so subsequent reads never need to handle `undefined`.
 */
function buildTimestampMap(): Map<ModelId, number[]> {
  const map = new Map<ModelId, number[]>();
  for (const modelId of FALLBACK_CHAIN) {
    map.set(modelId, []);
  }
  return map;
}

/**
 * Drop entries from `times` whose timestamp is older than `cutoff`.
 *
 * Mutates the array in place by truncating from the front; this keeps
 * the chronological order intact and avoids reallocating on every
 * call. Returns the post-prune length so the caller can write it
 * straight back to the counter field.
 */
function pruneOlderThan(times: number[], cutoff: number): number {
  let dropCount = 0;
  for (const t of times) {
    if (t >= cutoff) {
      break;
    }
    dropCount += 1;
  }
  if (dropCount > 0) {
    times.splice(0, dropCount);
  }
  return times.length;
}

/**
 * Build a `GeminiRequest` from `ExecuteRequestArgs`, conditionally
 * attaching the optional `signal`.
 *
 * `exactOptionalPropertyTypes` forbids spreading an `undefined`
 * `signal` into the request, so we branch instead.
 */
function buildGeminiRequest(
  modelId: ModelId,
  systemPrompt: string,
  contents: GeminiContent[],
  stream: boolean,
  signal: AbortSignal | undefined,
  useWebSearch: boolean,
): GeminiRequest {
  const base = {
    modelId,
    systemInstruction: systemPrompt,
    contents,
    stream,
    useWebSearch,
  };
  if (signal !== undefined) {
    return { ...base, signal };
  }
  return base;
}

// ---------------------------------------------------------------------------
// Per-attempt outcome
// ---------------------------------------------------------------------------

/**
 * Outcome of consuming one `sendGeminiRequest` generator.
 *
 * `chunks` carries any streamed text that arrived before a terminal
 * event so the caller can yield them out to the UI in chronological
 * order from its own generator (yielding cannot cross function
 * boundaries).
 */
type AttemptOutcome =
  | { kind: 'complete'; fullText: string; chunks: string[] }
  | { kind: 'rateLimit'; chunks: string[] }
  | { kind: 'transient'; chunks: string[] }
  | { kind: 'safetyBlock'; chunks: string[] }
  | { kind: 'streamInterrupted'; chunks: string[] };

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Build a Model_Router executor. The factory shape lets tests inject
 * a fake clock, a fake sleeper, a fake `sendGeminiRequest`, and a
 * fake API key for log redaction without monkey-patching modules.
 *
 * `initialState` is shallow-merged onto the default initial state so
 * a test can pre-populate cooldowns or counters without rebuilding
 * the entire `RouterState`.
 */
export function createModelRouterExecutor(opts?: {
  /** Defaults to the FALLBACK_CHAIN literal limits. */
  initialState?: Partial<RouterState>;
  /** Defaults to Date.now. Injectable for tests. */
  nowMs?: () => number;
  /** Defaults to a real-timer sleep. Injectable for tests. */
  sleep?: (ms: number) => Promise<void>;
  /** Defaults to sendGeminiRequest from infra/geminiClient. Injectable for tests. */
  sendRequest?: typeof sendGeminiRequest;
  /** API key for log redaction. Defaults to getApiKey(). */
  apiKey?: string | null;
}): ModelRouterExecutor {
  const nowMs = opts?.nowMs ?? ((): number => Date.now());
  const sleep = opts?.sleep ?? defaultSleep;
  const sendRequest = opts?.sendRequest ?? sendGeminiRequest;
  // `getApiKey()` is called once at construction so the redactor stays
  // pure even if the End_User rotates keys mid-session — the executor
  // is rebuilt by the App shell on key rotation anyway.
  const apiKey =
    opts !== undefined && Object.prototype.hasOwnProperty.call(opts, 'apiKey')
      ? (opts.apiKey ?? null)
      : getApiKey();

  // Build the default initial state and overlay any caller-supplied
  // partial state on top. The overlay is shallow; callers wanting to
  // override `health` for a single model should pass a complete
  // `health` record covering every ModelId.
  const baseState: RouterState = {
    health: buildInitialHealth(),
    forcedModel: null,
    log: [],
    nowMs: nowMs(),
  };
  let state: RouterState = { ...baseState, ...(opts?.initialState ?? {}) };

  // Sliding-window timestamp side-maps. These are NOT part of
  // `RouterState` because they are a wall-clock concern: we maintain
  // them imperatively so the pure reducer never has to model time as
  // an ordered sequence of decrement events.
  const lastMinuteTimes = buildTimestampMap();
  const lastDayTimes = buildTimestampMap();

  // ------------------------------------------------------------------
  // Persistence — restore counters/cooldowns from the previous tab
  // session so the app does not "forget" today's free-tier RPD across
  // a page reload (which would otherwise let it walk straight back into
  // a 429 from the API).
  // ------------------------------------------------------------------
  const persisted =
    opts?.initialState !== undefined ? null : loadPersistedHealth(state.nowMs);
  if (persisted !== null) {
    const restoredHealth = { ...state.health };
    for (const modelId of FALLBACK_CHAIN) {
      const entry = persisted[modelId];
      if (entry === undefined) continue;
      const prev = restoredHealth[modelId];
      if (prev === undefined) continue;
      const minuteList = lastMinuteTimes.get(modelId);
      const dayList = lastDayTimes.get(modelId);
      if (minuteList !== undefined) {
        minuteList.length = 0;
        for (const t of entry.minuteTimestamps) minuteList.push(t);
      }
      if (dayList !== undefined) {
        dayList.length = 0;
        for (const t of entry.dayTimestamps) dayList.push(t);
      }
      restoredHealth[modelId] = {
        ...prev,
        cooldownUntilMs: entry.cooldownUntilMs,
        requestsLastMinute: entry.minuteTimestamps.length,
        requestsLastDay: entry.dayTimestamps.length,
      };
    }
    state = { ...state, health: restoredHealth };
  }

  /**
   * Snapshot the current per-model timers/timestamps and write them
   * to durable storage. Best-effort; failures are swallowed by the
   * persistence layer.
   */
  const persistSnapshot = (): void => {
    if (typeof localStorage === 'undefined') return;
    const models: Partial<Record<ModelId, PersistedModelEntry>> = {};
    for (const modelId of FALLBACK_CHAIN) {
      const h = state.health[modelId];
      if (h === undefined) continue;
      models[modelId] = {
        cooldownUntilMs: h.cooldownUntilMs,
        minuteTimestamps: [...(lastMinuteTimes.get(modelId) ?? [])],
        dayTimestamps: [...(lastDayTimes.get(modelId) ?? [])],
      };
    }
    savePersistedHealth(state.nowMs, models);
  };

  /**
   * Apply `event` through the pure reducer and update the state in
   * place. Also pushes the timestamp onto the side maps when the
   * event is `success`, so the sliding-window refresh can re-derive
   * the absolute counter values on the next pass.
   */
  const applyEvent = (event: RouterEvent): void => {
    state = reduce(state, event, apiKey);
    if (event.kind === 'success') {
      const minuteList = lastMinuteTimes.get(event.modelId);
      const dayList = lastDayTimes.get(event.modelId);
      if (minuteList !== undefined) {
        minuteList.push(state.nowMs);
      }
      if (dayList !== undefined) {
        dayList.push(state.nowMs);
      }
    }
    // Persist after any event that materially affects future routing
    // decisions (counters or cooldowns). `tick` and `request` are
    // ignored — they don't change the persisted shape.
    if (
      event.kind === 'success' ||
      event.kind === 'rateLimit' ||
      event.kind === 'transient' ||
      event.kind === 'safetyBlock'
    ) {
      persistSnapshot();
    }
  };

  /**
   * Advance `state.nowMs` to the current wall clock by emitting a
   * `tick` event through the pure reducer. Called once per loop
   * iteration of `execute()` so `decide` always sees a fresh clock.
   */
  const tickClock = (): void => {
    applyEvent({ kind: 'tick', nowMs: nowMs() });
  };

  /**
   * Sliding-window refresh: prune timestamps older than 60 s / 24 h
   * and overwrite `state.health[m].requestsLastMinute /
   * requestsLastDay` with the resulting list lengths.
   *
   * This is intentionally a direct write on `state.health` rather
   * than a reducer event — there is no decrement event in
   * `RouterEvent`, by design (Property 13 covers the full reducer
   * surface and adding a decrement event would multiply the test
   * matrix without buying anything because counter aging is a wall-
   * clock derivative, not a state-machine transition).
   */
  const refreshCounters = (): void => {
    const now = state.nowMs;
    const minuteCutoff = now - ONE_MINUTE_MS;
    const dayCutoff = now - ONE_DAY_MS;
    const nextHealth: Record<ModelId, ModelHealth> = { ...state.health };
    let changed = false;

    for (const modelId of FALLBACK_CHAIN) {
      const minuteList = lastMinuteTimes.get(modelId);
      const dayList = lastDayTimes.get(modelId);
      if (minuteList === undefined || dayList === undefined) {
        continue;
      }
      const minuteCount = pruneOlderThan(minuteList, minuteCutoff);
      const dayCount = pruneOlderThan(dayList, dayCutoff);

      const prev = nextHealth[modelId];
      if (prev === undefined) {
        continue;
      }
      if (
        prev.requestsLastMinute === minuteCount &&
        prev.requestsLastDay === dayCount
      ) {
        continue;
      }
      nextHealth[modelId] = {
        ...prev,
        requestsLastMinute: minuteCount,
        requestsLastDay: dayCount,
      };
      changed = true;
    }

    if (changed) {
      state = { ...state, health: nextHealth };
    }
  };

  /**
   * Apply an in-memory, non-persisted cooldown to a single model after
   * a transient failure so `decide` advances to the next model in the
   * chain instead of re-selecting the same flaky model on the next loop
   * iteration. See {@link TRANSIENT_SKIP_MS} for the rationale.
   *
   * Only extends the cooldown — never shortens an existing (e.g. longer
   * rate-limit) cooldown that may already be in effect for the model.
   */
  const applyTransientSkip = (modelId: ModelId): void => {
    const prev = state.health[modelId];
    if (prev === undefined) return;
    const skipUntil = state.nowMs + TRANSIENT_SKIP_MS;
    if (prev.cooldownUntilMs >= skipUntil) return;
    state = {
      ...state,
      health: {
        ...state.health,
        [modelId]: { ...prev, cooldownUntilMs: skipUntil },
      },
    };
  };

  /**
   * Reset every model's cooldown and sliding-window counters to a clean
   * slate. Called when the executor rotates to a backup API key: the new
   * key has its own independent free-tier quota, so the cooldowns and
   * usage tallies accumulated under the previous key no longer apply.
   */
  const resetHealthForKeySwitch = (): void => {
    const freshHealth: Record<ModelId, ModelHealth> = { ...state.health };
    for (const modelId of FALLBACK_CHAIN) {
      const prev = freshHealth[modelId];
      if (prev === undefined) continue;
      freshHealth[modelId] = {
        ...prev,
        cooldownUntilMs: 0,
        requestsLastMinute: 0,
        requestsLastDay: 0,
      };
      lastMinuteTimes.get(modelId)?.splice(0);
      lastDayTimes.get(modelId)?.splice(0);
    }
    state = { ...state, health: freshHealth };
    persistSnapshot();
  };

  /**
   * Compute the wait until the soonest active cooldown expires, or
   * `0` when no model is in cooldown. Used by the giveUp-then-retry
   * policy (Requirement 5.5).
   */
  const computeSoonestCooldownWait = (): number => {
    let soonest = Number.POSITIVE_INFINITY;
    for (const modelId of FALLBACK_CHAIN) {
      const h = state.health[modelId];
      if (h === undefined) {
        continue;
      }
      if (h.cooldownUntilMs > state.nowMs && h.cooldownUntilMs < soonest) {
        soonest = h.cooldownUntilMs;
      }
    }
    if (!Number.isFinite(soonest)) {
      return 0;
    }
    return soonest - state.nowMs;
  };

  /**
   * Drive one `sendRequest` generator to completion.
   *
   * The Gemini client is documented to never throw (every failure
   * surfaces as a tagged event), but we still wrap the iteration in a
   * try/catch so an unexpected throw from a misbehaving injected fake
   * is converted into a `transient` outcome rather than escaping into
   * the UI (Requirement 5.6).
   *
   * The terminal event is mapped to the equivalent `RouterEvent` and
   * forwarded into `applyEvent`; the buffered chunks are returned
   * alongside so the outer generator can yield them in order.
   *
   * `streamInterrupted` is logged as a `transient` for diagnostics —
   * we deliberately do NOT set a cooldown for the model, because the
   * stream might have been killed by network conditions rather than
   * a model-specific failure. The caller still treats this as a
   * "move on to the next model" signal but without poisoning the
   * current model's cooldown.
   */
  const consumeAttempt = async (
    request: GeminiRequest,
    modelId: ModelId,
  ): Promise<AttemptOutcome> => {
    const chunks: string[] = [];

    let stream: AsyncGenerator<GeminiResponseEvent>;
    try {
      stream = sendRequest(request);
    } catch {
      applyEvent({ kind: 'transient', modelId });
      return { kind: 'transient', chunks };
    }

    try {
      for await (const event of stream) {
        if (event.kind === 'chunk') {
          chunks.push(event.text);
          continue;
        }

        if (event.kind === 'complete') {
          applyEvent({ kind: 'success', modelId });
          return { kind: 'complete', fullText: event.fullText, chunks };
        }

        if (event.kind === 'rateLimit') {
          const evt: RouterEvent =
            event.retryAfterSec !== undefined
              ? {
                  kind: 'rateLimit',
                  modelId,
                  retryAfterSec: event.retryAfterSec,
                }
              : { kind: 'rateLimit', modelId };
          applyEvent(evt);
          return { kind: 'rateLimit', chunks };
        }

        if (event.kind === 'transient') {
          applyEvent({ kind: 'transient', modelId });
          return { kind: 'transient', chunks };
        }

        if (event.kind === 'safetyBlock') {
          applyEvent({ kind: 'safetyBlock', modelId });
          return { kind: 'safetyBlock', chunks };
        }

        if (event.kind === 'streamInterrupted') {
          applyEvent({ kind: 'transient', modelId });
          return { kind: 'streamInterrupted', chunks };
        }
      }
      // Generator finished without a terminal event — treat as transient.
      applyEvent({ kind: 'transient', modelId });
      return { kind: 'transient', chunks };
    } catch {
      // Defense in depth: the Gemini client is documented to never
      // throw, but a misbehaving injected fake might.
      applyEvent({ kind: 'transient', modelId });
      return { kind: 'transient', chunks };
    }
  };

  // -------------------------------------------------------------------------
  // execute()
  // -------------------------------------------------------------------------

  /**
   * Run one user-message round through the fallback chain.
   *
   * Yields zero or more `chunk` events, optionally a `streamReplaced`,
   * and exactly one terminal event in `{ final, softFallback }`. Never
   * throws.
   */
  async function* execute(
    args: ExecuteRequestArgs,
  ): AsyncGenerator<ExecutorEvent> {
    let stream = args.stream;
    let safetyBlockCount = 0;
    let attemptedRetryAfterGiveUp = false;
    let streamWasInterrupted = false;
    let partialEmitted = false;
    // How many backup-key rotations are still available before we truly
    // give up. Each rotation resets every model's cooldown/counters so
    // the whole fallback chain is retried against a fresh free-tier quota.
    let keySwitchesRemaining = Math.max(0, getApiKeyCount() - 1);
    // Total attempt guard — guarantees termination (Property: execute
    // always produces a terminal event in bounded steps).
    let totalAttempts = 0;

    while (true) {
      tickClock();
      refreshCounters();

      const decision = decide(state);

      if (decision.kind === 'try') {
        const modelId = decision.modelId;
        totalAttempts += 1;
        if (totalAttempts > MAX_TOTAL_ATTEMPTS) {
          yield { kind: 'softFallback' };
          return;
        }
        applyEvent({ kind: 'request' });

        const request = buildGeminiRequest(
          modelId,
          args.systemPrompt,
          args.contents,
          stream,
          args.signal,
          args.useWebSearch === true,
        );

        const outcome = await consumeAttempt(request, modelId);

        // Flush any buffered chunks first so the UI sees them in order.
        for (const chunkText of outcome.chunks) {
          partialEmitted = true;
          yield { kind: 'chunk', text: chunkText };
        }

        if (outcome.kind === 'complete') {
          if (streamWasInterrupted && partialEmitted) {
            yield {
              kind: 'streamReplaced',
              fullText: outcome.fullText,
              modelUsed: modelId,
            };
          } else {
            yield {
              kind: 'final',
              finalText: outcome.fullText,
              modelUsed: modelId,
            };
          }
          return;
        }

        if (outcome.kind === 'transient') {
          // Briefly skip this model so the chain advances to the next
          // one instead of re-selecting the same flaky model. Prevents
          // a reliably-503ing model (e.g. gemini-3.5-flash) from
          // trapping the executor (Requirement 5.4, hardened).
          applyTransientSkip(modelId);
          await sleep(TRANSIENT_BACKOFF_MS);
          continue;
        }

        if (outcome.kind === 'safetyBlock') {
          safetyBlockCount += 1;
          if (safetyBlockCount >= MAX_SAFETY_BLOCKS) {
            yield { kind: 'softFallback' };
            return;
          }
          // Skip this model for the rest of the turn so the next loop
          // iteration tries a *different* model rather than blocking on
          // the same one again.
          applyTransientSkip(modelId);
          continue;
        }

        if (outcome.kind === 'streamInterrupted') {
          streamWasInterrupted = true;
          stream = false;
          applyTransientSkip(modelId);
          continue;
        }

        // 'rateLimit' falls through with no extra delay (Requirement 5.3).
        continue;
      }

      if (decision.kind === 'wait') {
        await sleep(decision.waitMs);
        continue;
      }

      // 'giveUp' — before surfacing soft-fallback, try rotating to a
      // backup API key. Each backup has its own free-tier quota, so a
      // fresh key lets the entire fallback chain run again even when the
      // current key is fully rate-limited (Requirement 5.5, extended).
      if (keySwitchesRemaining > 0 && advanceApiKey()) {
        keySwitchesRemaining -= 1;
        resetHealthForKeySwitch();
        // Reset the give-up retry latch so the new key gets the full
        // wait-then-retry treatment if it, too, runs dry.
        attemptedRetryAfterGiveUp = false;
        continue;
      }

      // No more keys to rotate to — apply the wait-then-retry-once
      // policy (Requirement 5.5).
      if (!attemptedRetryAfterGiveUp) {
        attemptedRetryAfterGiveUp = true;
        const cooldownWait = computeSoonestCooldownWait();
        const bounded = Math.min(
          Math.max(cooldownWait, 0),
          MAX_GIVEUP_WAIT_MS,
        );
        if (bounded > 0) {
          await sleep(bounded);
        }
        continue;
      }

      yield { kind: 'softFallback' };
      return;
    }
  }

  // -------------------------------------------------------------------------
  // oneShot()
  // -------------------------------------------------------------------------

  /**
   * One-shot, non-streaming convenience used by the Rolling_Summarizer
   * (Task 4.3). Wraps `userText` in a single `user` content turn, runs
   * the full executor pipeline, and collapses the event stream into a
   * single `RouterResult`.
   *
   * `preferredModel` temporarily pins the executor to a specific
   * model for the duration of this call only. The previous
   * `forcedModel` value is restored before return so the global
   * forced-model override (Requirement 13.4) is not affected.
   */
  async function oneShot(args: {
    systemPrompt: string;
    userText: string;
    preferredModel?: ModelId;
  }): Promise<RouterResult> {
    const previousForced = state.forcedModel;
    if (args.preferredModel !== undefined) {
      setForcedModel(args.preferredModel);
    }

    let result: RouterResult = { ok: false, kind: 'softFallback' };
    try {
      const userTurn: GeminiContent = {
        role: 'user',
        parts: [{ text: args.userText }],
      };
      const gen = execute({
        systemPrompt: args.systemPrompt,
        contents: [userTurn],
        stream: false,
      });

      for await (const event of gen) {
        if (
          event.kind === 'final' &&
          event.finalText !== undefined &&
          event.modelUsed !== undefined
        ) {
          result = {
            ok: true,
            text: event.finalText,
            modelUsed: event.modelUsed,
          };
          break;
        }
        if (
          event.kind === 'streamReplaced' &&
          event.fullText !== undefined &&
          event.modelUsed !== undefined
        ) {
          result = {
            ok: true,
            text: event.fullText,
            modelUsed: event.modelUsed,
          };
          break;
        }
        if (event.kind === 'softFallback') {
          result = { ok: false, kind: 'softFallback' };
          break;
        }
      }
    } finally {
      // Restore even if the generator threw; this catch-all is safety
      // net only — `execute` is documented to never throw.
      if (args.preferredModel !== undefined) {
        setForcedModel(previousForced);
      }
    }

    return result;
  }

  // -------------------------------------------------------------------------
  // Forced-model override
  // -------------------------------------------------------------------------

  /**
   * Set or clear the developer override (Requirement 13.4). The
   * pure reducer does not expose a `setForcedModel` event because
   * `forcedModel` is a control-plane concern, not part of the health
   * state-machine; mutating it directly here keeps the reducer
   * surface narrow.
   */
  function setForcedModel(modelId: ModelId | null): void {
    state = { ...state, forcedModel: modelId };
  }

  // -------------------------------------------------------------------------
  // getState()
  // -------------------------------------------------------------------------

  function getState(): RouterState {
    return state;
  }

  return { getState, setForcedModel, execute, oneShot };
}
